// Assessment Scoring Utility
// Calculates maturity scores from form responses and provides recommendations

/**
 * Calculate dimension scores from form responses
 * @param {Object} responses - All form responses
 * @returns {Object} - Scores for each dimension
 */
export const calculateDimensionScores = (responses) => {
  const scores = {
    strategy: calculateStrategyScore(responses.strategy_dimension || {}),
    data: calculateDataScore(responses.data_dimension || {}),
    technology: calculateTechnologyScore(responses.technology_dimension || {}, responses.current_state || {}),
    people: calculatePeopleScore(responses.people_dimension || {}, responses.current_state || {}),
    governance: calculateGovernanceScore(responses.governance_dimension || {}),
  };

  // Calculate overall score as average
  const overall = (
    scores.strategy +
    scores.data +
    scores.technology +
    scores.people +
    scores.governance
  ) / 5;

  return {
    ...scores,
    overall: parseFloat(overall.toFixed(2)),
  };
};

/**
 * Map radio option index to score (0-based index maps to 1-5 score)
 * Radio options are typically 0-3 or 0-4, so we need to scale appropriately
 */
const mapRadioToScore = (value, maxOptions = 4) => {
  // Convert to number index
  const index = typeof value === 'string' ? parseInt(value) : value;
  
  if (isNaN(index) || index === undefined || index === null) return 1;
  
  // Scale 0-based index to 1-5 score based on number of options
  // For 4 options: 0→2, 1→3, 2→4, 3→5
  // For 5 options: 0→1, 1→2, 2→3, 3→4, 4→5
  return Math.min(5, Math.max(1, Math.ceil(((index + 1) / maxOptions) * 5)));
};

/**
 * Calculate strategy dimension score
 */
const calculateStrategyScore = (responses) => {
  const scores = [];

  console.log('=== STRATEGY SCORING DEBUG ===');
  console.log('Raw responses:', responses);

  // AI strategy exists (radio with 4 options: 0-3 → map to 1-5)
  if (responses.ai_strategy_exists !== undefined) {
    const fieldScore = mapRadioToScore(responses.ai_strategy_exists, 4);
    console.log('ai_strategy_exists:', responses.ai_strategy_exists, '→', fieldScore);
    scores.push(fieldScore);
  }

  // Business alignment (scale 1-5) - direct score
  if (responses.business_alignment !== undefined) {
    const score = parseInt(responses.business_alignment) || 1;
    console.log('business_alignment:', responses.business_alignment, '→', score);
    scores.push(Math.min(5, Math.max(1, score)));
  }

  // Success metrics (radio with 4 options)
  if (responses.success_metrics !== undefined) {
    const fieldScore = mapRadioToScore(responses.success_metrics, 4);
    console.log('success_metrics:', responses.success_metrics, '→', fieldScore);
    scores.push(fieldScore);
  }

  // Executive sponsorship (radio with 4 options)
  if (responses.executive_sponsorship !== undefined) {
    const fieldScore = mapRadioToScore(responses.executive_sponsorship, 4);
    console.log('executive_sponsorship:', responses.executive_sponsorship, '→', fieldScore);
    scores.push(fieldScore);
  }

  const average = scores.length > 0
    ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2))
    : 1;
  
  console.log('Strategy scores array:', scores);
  console.log('Strategy average:', average);
  console.log('=== END STRATEGY SCORING ===');

  return average;
};

/**
 * Calculate data dimension score
 */
const calculateDataScore = (responses) => {
  const scores = [];

  // Data inventory (radio with 5 options: 0-4 → map to 1-5)
  if (responses.data_inventory !== undefined) {
    scores.push(mapRadioToScore(responses.data_inventory, 5));
  }

  // Data quality (scale 1-5) - direct score
  if (responses.data_quality !== undefined) {
    const score = parseInt(responses.data_quality) || 1;
    scores.push(Math.min(5, Math.max(1, score)));
  }

  // Data integration (radio with 4 options)
  if (responses.data_integration !== undefined) {
    scores.push(mapRadioToScore(responses.data_integration, 4));
  }

  // Data security (checkbox - count checked / total * 5)
  if (responses.data_security && Array.isArray(responses.data_security)) {
    const hasNone = responses.data_security.includes('None of the above');
    if (hasNone) {
      scores.push(1);
    } else {
      const totalOptions = 7; // Total security options (excluding "None")
      const checkedCount = responses.data_security.length;
      const score = Math.min(5, Math.max(1, (checkedCount / totalOptions) * 5));
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  // Structured vs unstructured data types (checkbox - more types = better)
  if (responses.structured_vs_unstructured && Array.isArray(responses.structured_vs_unstructured)) {
    const totalTypes = 8;
    const selectedCount = responses.structured_vs_unstructured.length;
    const score = Math.min(5, Math.max(1, (selectedCount / totalTypes) * 4 + 1));
    scores.push(parseFloat(score.toFixed(2)));
  }

  // Data cleansing needed (radio with 5 options: 0-4) - INVERSE scoring
  // "No - data is ready" (index 3) should give highest score
  // "Yes - significant cleansing required" (index 0) should give lowest score
  if (responses.data_cleansing_needed !== undefined) {
    const index = typeof responses.data_cleansing_needed === 'string' ? 
      parseInt(responses.data_cleansing_needed) : responses.data_cleansing_needed;
    if (!isNaN(index)) {
      // Map: 0→1, 1→2, 2→3, 3→5, 4→3 (Unsure gets middle score)
      const cleansingScoreMap = [1, 2, 3, 5, 3];
      scores.push(cleansingScoreMap[index] || 1);
    }
  }

  return scores.length > 0
    ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2))
    : 1;
};

/**
 * Calculate technology dimension score
 */
const calculateTechnologyScore = (responses, currentState) => {
  const scores = [];

  // Cloud readiness (radio with 4 options)
  if (responses.cloud_readiness !== undefined) {
    scores.push(mapRadioToScore(responses.cloud_readiness, 4));
  }

  // Cloud providers (checkbox - more providers = more mature multi-cloud)
  if (responses.cloud_providers && Array.isArray(responses.cloud_providers)) {
    const hasOnPremOnly = responses.cloud_providers.includes('On-premises only');
    const hasNotDetermined = responses.cloud_providers.includes('Not yet determined');
    if (hasOnPremOnly || hasNotDetermined) {
      scores.push(2);
    } else {
      const providerCount = responses.cloud_providers.filter(p => 
        p !== 'On-premises only' && p !== 'Not yet determined' && p !== 'Other cloud providers'
      ).length;
      scores.push(Math.min(5, Math.max(2, providerCount + 2)));
    }
  }

  // ERP systems (checkbox - more integrated systems = better)
  if (responses.erp_systems && Array.isArray(responses.erp_systems)) {
    const systemCount = responses.erp_systems.length;
    const score = Math.min(5, Math.max(1, (systemCount / 5) * 3 + 1));
    scores.push(parseFloat(score.toFixed(2)));
  }

  // API maturity / integration level (radio with 4 options)
  if (responses.api_maturity !== undefined) {
    scores.push(mapRadioToScore(responses.api_maturity, 4));
  }

  // Dev resources (radio with 5 options)
  if (responses.dev_resources !== undefined) {
    scores.push(mapRadioToScore(responses.dev_resources, 5));
  }

  // Infrastructure constraints (checkbox - INVERSE: fewer constraints = higher score)
  if (responses.infrastructure_constraints && Array.isArray(responses.infrastructure_constraints)) {
    const hasNoConstraints = responses.infrastructure_constraints.includes('No major constraints');
    if (hasNoConstraints) {
      scores.push(5);
    } else {
      const constraintCount = responses.infrastructure_constraints.length;
      const score = Math.max(1, 5 - (constraintCount * 0.6));
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  // AI tools in use (from current_state section - checkbox)
  if (currentState.ai_tools_in_use && Array.isArray(currentState.ai_tools_in_use)) {
    const hasNone = currentState.ai_tools_in_use.includes('None');
    if (hasNone) {
      scores.push(1);
    } else {
      const toolCount = currentState.ai_tools_in_use.length;
      const score = Math.min(5, Math.max(1, (toolCount / 6) * 4 + 1));
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  return scores.length > 0
    ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2))
    : 1;
};

/**
 * Calculate people dimension score
 */
const calculatePeopleScore = (responses, currentState) => {
  const scores = [];

  // AI literacy (radio with 5 options)
  if (responses.ai_literacy !== undefined) {
    scores.push(mapRadioToScore(responses.ai_literacy, 5));
  }

  // Training provided (radio with 4 options) - NOT a checkbox!
  if (responses.training_provided !== undefined) {
    scores.push(mapRadioToScore(responses.training_provided, 4));
  }

  // Change readiness (scale 1-5) - direct score
  if (responses.change_readiness !== undefined) {
    const score = parseInt(responses.change_readiness) || 1;
    scores.push(Math.min(5, Math.max(1, score)));
  }

  // Dedicated roles (checkbox - having roles is positive)
  if (responses.dedicated_roles && Array.isArray(responses.dedicated_roles)) {
    const hasNoRoles = responses.dedicated_roles.includes('No dedicated AI roles');
    if (hasNoRoles) {
      scores.push(1);
    } else {
      const roleCount = responses.dedicated_roles.length;
      const score = Math.min(5, (roleCount / 5) * 4 + 1);
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  // AI usage scope (from current_state - select with 6 options)
  if (currentState.ai_usage_scope !== undefined && currentState.ai_usage_scope !== 'Unknown') {
    // Map usage scope to score: Less than 5% → 1, More than 75% → 5
    const scopeScores = {
      'Less than 5%': 1,
      '5-25%': 2,
      '25-50%': 3,
      '50-75%': 4,
      'More than 75%': 5,
      'Unknown': 2.5
    };
    scores.push(scopeScores[currentState.ai_usage_scope] || 2.5);
  }

  // Staff burden concerns (checkbox - INVERSE: fewer concerns = higher score)
  if (responses.staff_burden_concerns && Array.isArray(responses.staff_burden_concerns)) {
    const hasNoConcerns = responses.staff_burden_concerns.includes('No major concerns');
    if (hasNoConcerns) {
      scores.push(5);
    } else {
      const concernCount = responses.staff_burden_concerns.length;
      const score = Math.max(1, 5 - (concernCount * 0.5));
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  return scores.length > 0
    ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2))
    : 1;
};

/**
 * Calculate governance dimension score
 */
const calculateGovernanceScore = (responses) => {
  const scores = [];

  // Governance framework / change management (radio with 4 options)
  if (responses.governance_framework !== undefined) {
    scores.push(mapRadioToScore(responses.governance_framework, 4));
  }

  // Ethical guidelines (radio with 5 options)
  if (responses.ethical_guidelines !== undefined) {
    scores.push(mapRadioToScore(responses.ethical_guidelines, 5));
  }

  // Compliance requirements (checkbox - having compliance is positive)
  if (responses.compliance_requirements && Array.isArray(responses.compliance_requirements)) {
    const hasNone = responses.compliance_requirements.includes("None");
    if (hasNone) {
      scores.push(2); // Neutral score - not having requirements isn't bad
    } else {
      const complianceCount = responses.compliance_requirements.length;
      const score = Math.min(5, (complianceCount / 5) * 3 + 2);
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  // Risk assessment (radio with 5 options)
  if (responses.risk_assessment !== undefined) {
    scores.push(mapRadioToScore(responses.risk_assessment, 5));
  }

  // Accountability (radio with 5 options)
  if (responses.accountability !== undefined) {
    scores.push(mapRadioToScore(responses.accountability, 5));
  }

  // Data governance (checkbox - count practices)
  if (responses.data_governance && Array.isArray(responses.data_governance)) {
    const hasLimited = responses.data_governance.includes('Limited or no data governance');
    if (hasLimited) {
      scores.push(1);
    } else {
      const practiceCount = responses.data_governance.length;
      const score = Math.min(5, (practiceCount / 6) * 4 + 1);
      scores.push(parseFloat(score.toFixed(2)));
    }
  }

  return scores.length > 0
    ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2))
    : 1;
};

/**
 * Determine maturity stage based on overall score
 * @param {number} overallScore - Overall maturity score (1-5)
 * @returns {Object} - Stage information
 */
export const determineMaturityStage = (overallScore) => {
  if (overallScore < 1.5) {
    return {
      stage: 1,
      name: 'AI Access',
      subtitle: 'AI + Resources',
      description: 'Foundational access to AI systems, models, and data. The organization is experimenting and learning.',
      color: 'blue'
    };
  } else if (overallScore < 2.5) {
    return {
      stage: 2,
      name: 'AI Integration',
      subtitle: 'AI + Tools',
      description: 'AI capabilities become embedded within systems and workflows to enhance productivity and collaboration.',
      color: 'green'
    };
  } else if (overallScore < 3.5) {
    return {
      stage: 3,
      name: 'AI Orchestration',
      subtitle: 'AI + Workflows',
      description: 'The organization coordinates AI and automation through orchestration systems, with AI handling multi-step processes under human oversight.',
      color: 'purple'
    };
  } else {
    return {
      stage: 4,
      name: 'AI Transformation',
      subtitle: 'AI + Mission',
      description: 'AI is systematically integrated across core operations. AI provides scaled execution and intelligence while humans maintain strategic decision authority and accountability.',
      color: 'orange'
    };
  }
};

/**
 * Generate gap analysis and recommendations
 * @param {Object} scores - Dimension scores
 * @param {string} currentStage - Self-assessed current stage
 * @param {string} targetStage - Desired target stage
 * @returns {Object} - Gap analysis and recommendations
 */
export const generateRecommendations = (scores, currentStage, targetStage) => {
  const weakestDimensions = Object.entries(scores)
    .filter(([key]) => key !== 'overall')
    .sort((a, b) => a[1] - b[1])
    .slice(0, 2)
    .map(([key]) => key);

  const recommendations = [];

  // Dimension-specific recommendations
  weakestDimensions.forEach(dimension => {
    switch (dimension) {
      case 'strategy':
        recommendations.push({
          dimension: 'Strategy',
          priority: 'High',
          action: 'Develop a formal AI strategy document aligned with business objectives',
          impact: 'Provides clear direction and executive buy-in for AI initiatives'
        });
        break;
      case 'data':
        recommendations.push({
          dimension: 'Data',
          priority: 'High',
          action: 'Conduct a comprehensive data inventory and quality assessment',
          impact: 'Ensures AI systems have access to quality, well-governed data'
        });
        break;
      case 'technology':
        recommendations.push({
          dimension: 'Technology',
          priority: 'Medium',
          action: 'Assess cloud readiness and API infrastructure for AI integration',
          impact: 'Enables scalable AI deployment and system integration'
        });
        break;
      case 'people':
        recommendations.push({
          dimension: 'People',
          priority: 'High',
          action: 'Implement AI literacy training and change management programs',
          impact: 'Builds organizational capacity and reduces resistance to AI adoption'
        });
        break;
      case 'governance':
        recommendations.push({
          dimension: 'Governance',
          priority: 'Medium',
          action: 'Establish AI governance framework with ethical guidelines and accountability',
          impact: 'Ensures responsible AI use and regulatory compliance'
        });
        break;
      default:
        break;
    }
  });

  // Stage-specific recommendations
  const calculatedStage = determineMaturityStage(scores.overall);
  if (targetStage && targetStage !== currentStage) {
    recommendations.push({
      dimension: 'Overall',
      priority: 'High',
      action: `Focus on advancing from ${calculatedStage.name} to ${targetStage}`,
      impact: 'Aligns efforts with strategic AI maturity goals'
    });
  }

  return {
    weakestDimensions,
    recommendations,
    calculatedStage,
    gaps: {
      strategy: 5 - scores.strategy,
      data: 5 - scores.data,
      technology: 5 - scores.technology,
      people: 5 - scores.people,
      governance: 5 - scores.governance,
    }
  };
};

/**
 * Format scores for display
 * @param {Object} scores - Raw scores
 * @returns {Object} - Formatted scores with labels
 */
export const formatScoresForDisplay = (scores) => {
  return {
    dimensions: [
      { name: 'Strategy', score: scores.strategy, color: '#3b82f6' },
      { name: 'Data', score: scores.data, color: '#10b981' },
      { name: 'Technology', score: scores.technology, color: '#8b5cf6' },
      { name: 'People', score: scores.people, color: '#f59e0b' },
      { name: 'Governance', score: scores.governance, color: '#ef4444' },
    ],
    overall: scores.overall,
  };
};
